import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
  CircularProgress,
  FormControl,
  InputLabel,
  InputAdornment,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Upload as UploadIcon,
  Info as InfoIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import Modal from '../../../components/Modals/Modal/Modal';
import adsManagerService, { DuplicateAdRequest, AdFullDetails } from '../../../services/adsManager.service';

interface DuplicateAdModalProps {
  open: boolean;
  adId: string;
  adName: string;
  onClose: () => void;
  onConfirm: (request: DuplicateAdRequest) => Promise<void>;
}

/**
 * DuplicateAdModal - Comprehensive ad duplication wizard
 *
 * Features:
 * - Show original ad details with creative image
 * - Upload new image option
 * - Configure ad name
 * - Choose same adset or create new adset
 * - Bid strategy configuration
 * - Budget multiplier or fixed budget
 * - Initial status selection
 * - Summary preview
 */
export const DuplicateAdModal: React.FC<DuplicateAdModalProps> = ({
  open,
  adId,
  adName: originalAdName,
  onClose,
  onConfirm,
}) => {
  // Form state
  const [newAdName, setNewAdName] = useState('');
  const [budgetMode, setBudgetMode] = useState<'multiplier' | 'fixed'>('multiplier');
  const [budgetMultiplier, setBudgetMultiplier] = useState(1.0);
  const [fixedBudget, setFixedBudget] = useState(0);
  const [initialStatus, setInitialStatus] = useState<'ACTIVE' | 'PAUSED'>('PAUSED');
  const [createNewAdset, setCreateNewAdset] = useState(true);
  const [newAdsetName, setNewAdsetName] = useState('');
  const [bidStrategy, setBidStrategy] = useState('LOWEST_COST_WITHOUT_CAP');
  const [bidAmount, setBidAmount] = useState('');
  const [minRoasTarget, setMinRoasTarget] = useState('');
  const [uploadedImage, setUploadedImage] = useState<File | null>(null);
  const [uploadedImagePreview, setUploadedImagePreview] = useState<string | null>(null);

  // Loading states
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fullDetails, setFullDetails] = useState<AdFullDetails | null>(null);

  // Fetch full ad details when modal opens
  useEffect(() => {
    if (open && adId) {
      // Reset form
      setNewAdName(originalAdName + ' - Copy');
      setBudgetMode('multiplier');
      setBudgetMultiplier(1.0);
      setInitialStatus('PAUSED');
      setCreateNewAdset(true);
      setUploadedImage(null);
      setUploadedImagePreview(null);
      setBidAmount('');
      setMinRoasTarget('');

      // Fetch full details
      const fetchDetails = async () => {
        setIsLoadingDetails(true);
        try {
          const data = await adsManagerService.getAdFullDetails(adId);
          setFullDetails(data);

          // Set default values from fetched data
          const currentBudget = parseFloat(data.adset.daily_budget || '0') / 100; // Convert from cents
          setFixedBudget(currentBudget);
          setBidStrategy(data.adset.bid_strategy || 'LOWEST_COST_WITHOUT_CAP');

          if (data.adset.bid_amount) {
            setBidAmount((parseFloat(data.adset.bid_amount) / 100).toFixed(2));
          }

          // Set new adset name with today's date
          const today = new Date().toISOString().split('T')[0];
          setNewAdsetName(`${data.adset.name} - ${today}`);
        } catch (error) {
          console.error('Failed to fetch ad details:', error);
          // Set defaults on error
          setFixedBudget(0);
          setBidStrategy('LOWEST_COST_WITHOUT_CAP');
          const today = new Date().toISOString().split('T')[0];
          setNewAdsetName(`${originalAdName} - ${today}`);
        } finally {
          setIsLoadingDetails(false);
        }
      };

      fetchDetails();
    }
  }, [open, adId, originalAdName]);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    if (!newAdName.trim()) return;

    setIsSubmitting(true);
    try {
      const currentBudget = parseFloat(fullDetails?.adset.daily_budget || '0') / 100;
      const actualBudgetMultiplier =
        budgetMode === 'fixed'
          ? currentBudget > 0
            ? fixedBudget / currentBudget
            : 1.0
          : budgetMultiplier;

      const request: DuplicateAdRequest = {
        newName: newAdName,
        budgetMultiplier: actualBudgetMultiplier,
        status: initialStatus,
        createNewAdset,
        ...(createNewAdset && {
          newAdsetName,
          bidStrategy,
          ...(bidAmount && { bidAmount: parseFloat(bidAmount) }),
          ...(minRoasTarget && { minRoasTarget: parseFloat(minRoasTarget) }),
        }),
      };

      await onConfirm(request);
      onClose();
    } catch (error) {
      console.error('Duplication error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calculate values
  const currentBudget = parseFloat(fullDetails?.adset?.daily_budget || '0') / 100;
  const newBudget = budgetMode === 'fixed' ? fixedBudget : currentBudget * budgetMultiplier;
  const budgetChange = newBudget - currentBudget;
  const budgetChangePercent = currentBudget > 0 ? (budgetChange / currentBudget) * 100 : 0;

  // Use thumbnail URL directly from API (same as in AdRow)
  const creativeImageUrl = fullDetails?.ad?.creative?.thumbnail_url || fullDetails?.ad?.creative?.image_url;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Duplicate Ad"
      maxWidth={900}
    >
      {isLoadingDetails ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Original Ad Info */}
            <Box sx={{ bgcolor: 'grey.50', borderRadius: 2, p: 2, border: 1, borderColor: 'divider', mt: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <InfoIcon fontSize="small" color="primary" />
                Original Ad
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                {/* Creative Image */}
                <Box sx={{ flexShrink: 0 }}>
                  <Box
                    sx={{
                      width: 96,
                      height: 96,
                      borderRadius: 2,
                      overflow: 'hidden',
                      border: 2,
                      borderColor: 'divider',
                      bgcolor: 'grey.100',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {uploadedImagePreview ? (
                      <img
                        src={uploadedImagePreview}
                        alt="Uploaded"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : creativeImageUrl ? (
                      <img
                        src={creativeImageUrl}
                        alt="Ad Creative"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary" textAlign="center">
                        No Image
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="caption" display="block" textAlign="center" sx={{ mt: 0.5 }}>
                    {uploadedImagePreview ? 'New Image' : 'Original'}
                  </Typography>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                    id="image-upload-input"
                  />
                  <label htmlFor="image-upload-input">
                    <Button
                      component="span"
                      size="small"
                      variant="outlined"
                      fullWidth
                      sx={{ mt: 1, textTransform: 'none', fontSize: '0.75rem' }}
                      startIcon={<UploadIcon fontSize="small" />}
                    >
                      {uploadedImage ? 'Change' : 'Upload'}
                    </Button>
                  </label>
                </Box>

                {/* Ad Details */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5, fontSize: '0.875rem' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Name:</Typography>
                    <Typography variant="body2" fontWeight="medium" noWrap sx={{ maxWidth: 300 }}>
                      {fullDetails?.ad?.name || originalAdName}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Campaign:</Typography>
                    <Typography variant="body2" fontWeight="medium" noWrap sx={{ maxWidth: 300 }}>
                      {fullDetails?.campaign?.name || '-'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">AdSet:</Typography>
                    <Typography variant="body2" fontWeight="medium" noWrap sx={{ maxWidth: 300 }}>
                      {fullDetails?.adset?.name || '-'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Current Budget:</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      ${currentBudget.toFixed(2)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Status:</Typography>
                    <Typography
                      variant="body2"
                      fontWeight="medium"
                      color={fullDetails?.ad?.status === 'ACTIVE' ? 'success.main' : 'text.secondary'}
                    >
                      {fullDetails?.ad?.status || '-'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* New Ad Name */}
            <TextField
              label="New Ad Name"
              value={newAdName}
              onChange={(e) => setNewAdName(e.target.value)}
              required
              fullWidth
              helperText="Give your duplicate ad a unique name"
            />

            {/* AdSet Configuration */}
            <Box>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
                AdSet Configuration
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box
                  onClick={() => setCreateNewAdset(false)}
                  sx={{
                    p: 2,
                    border: 2,
                    borderColor: !createNewAdset ? 'primary.main' : 'divider',
                    bgcolor: !createNewAdset ? 'primary.50' : 'background.paper',
                    borderRadius: 2,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': { borderColor: !createNewAdset ? 'primary.dark' : 'grey.400' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'start', gap: 1.5 }}>
                    <Radio checked={!createNewAdset} sx={{ p: 0, mt: 0.25 }} />
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        Same AdSet
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Duplicate ad in existing adset: {fullDetails?.adset?.name || '-'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box
                  onClick={() => setCreateNewAdset(true)}
                  sx={{
                    p: 2,
                    border: 2,
                    borderColor: createNewAdset ? 'success.main' : 'divider',
                    bgcolor: createNewAdset ? 'success.50' : 'background.paper',
                    borderRadius: 2,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': { borderColor: createNewAdset ? 'success.dark' : 'grey.400' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'start', gap: 1.5 }}>
                    <Radio checked={createNewAdset} sx={{ p: 0, mt: 0.25 }} />
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" fontWeight="medium">
                          New AdSet
                        </Typography>
                        <Box
                          sx={{
                            px: 1,
                            py: 0.25,
                            bgcolor: 'success.main',
                            color: 'white',
                            borderRadius: 10,
                            fontSize: '0.65rem',
                            fontWeight: 'bold',
                          }}
                        >
                          Recommended
                        </Box>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Create a new adset with same settings and updated budget
                      </Typography>
                      {createNewAdset && (
                        <TextField
                          value={newAdsetName}
                          onChange={(e) => setNewAdsetName(e.target.value)}
                          required
                          fullWidth
                          size="small"
                          sx={{ mt: 1.5 }}
                          placeholder="Enter new adset name..."
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Bid Strategy (when creating new adset) */}
            {createNewAdset && (
              <>
                <FormControl fullWidth>
                  <InputLabel>Bid Strategy</InputLabel>
                  <Select
                    value={bidStrategy}
                    onChange={(e) => setBidStrategy(e.target.value)}
                    label="Bid Strategy"
                  >
                    <MenuItem value="LOWEST_COST_WITHOUT_CAP">Lowest Cost (No Cap)</MenuItem>
                    <MenuItem value="LOWEST_COST_WITH_BID_CAP">Lowest Cost with Bid Cap</MenuItem>
                    <MenuItem value="COST_CAP">Cost Cap</MenuItem>
                    <MenuItem value="LOWEST_COST_WITH_MIN_ROAS">Lowest Cost with Min ROAS</MenuItem>
                  </Select>
                </FormControl>

                {/* Bid Amount */}
                {(bidStrategy === 'LOWEST_COST_WITH_BID_CAP' || bidStrategy === 'COST_CAP') && (
                  <TextField
                    label={bidStrategy === 'COST_CAP' ? 'Cost Cap Amount' : 'Bid Cap Amount'}
                    value={bidAmount}
                    onChange={(e) => setBidAmount(e.target.value)}
                    type="number"
                    required
                    fullWidth
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                    helperText={
                      bidStrategy === 'COST_CAP'
                        ? 'Maximum cost you want to pay per result on average'
                        : 'Maximum bid you want to place in each auction'
                    }
                  />
                )}

                {/* Min ROAS Target */}
                {bidStrategy === 'LOWEST_COST_WITH_MIN_ROAS' && (
                  <TextField
                    label="Minimum ROAS Target"
                    value={minRoasTarget}
                    onChange={(e) => setMinRoasTarget(e.target.value)}
                    type="number"
                    required
                    fullWidth
                    helperText="Enter as ratio (e.g., 2.50 means $2.50 revenue per $1 spent = 250% ROAS)"
                  />
                )}
              </>
            )}

            {/* Budget Configuration */}
            <Box>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
                Budget Configuration
              </Typography>
              <RadioGroup value={budgetMode} onChange={(e) => setBudgetMode(e.target.value as any)}>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <FormControlLabel value="multiplier" control={<Radio />} label="Budget Multiplier" />
                  <FormControlLabel value="fixed" control={<Radio />} label="Fixed Budget" />
                </Box>
              </RadioGroup>

              {budgetMode === 'multiplier' ? (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TextField
                      type="number"
                      value={budgetMultiplier}
                      onChange={(e) => setBudgetMultiplier(parseFloat(e.target.value) || 1.0)}
                      inputProps={{ step: 0.1, min: 0.1, max: 10 }}
                      sx={{ width: 120 }}
                    />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {[0.5, 1.0, 1.5, 2.0].map((val) => (
                        <Button
                          key={val}
                          size="small"
                          variant="outlined"
                          onClick={() => setBudgetMultiplier(val)}
                        >
                          {val}x
                        </Button>
                      ))}
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TextField
                      type="number"
                      value={fixedBudget}
                      onChange={(e) => setFixedBudget(parseFloat(e.target.value) || 0)}
                      inputProps={{ step: 1, min: 1 }}
                      fullWidth
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                    />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {[50, 100, 200].map((val) => (
                        <Button
                          key={val}
                          size="small"
                          variant="outlined"
                          onClick={() => setFixedBudget(val)}
                        >
                          ${val}
                        </Button>
                      ))}
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Budget Summary */}
              <Box sx={{ mt: 2, p: 2, bgcolor: 'info.50', borderRadius: 1, border: 1, borderColor: 'info.200' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2">Current Budget:</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    ${currentBudget.toFixed(2)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">New Budget:</Typography>
                  <Typography variant="body1" fontWeight="bold" color="primary.main">
                    ${newBudget.toFixed(2)}
                  </Typography>
                </Box>
                {newBudget !== currentBudget && (
                  <Typography variant="caption" color="text.secondary">
                    {budgetChange > 0 ? '+' : ''}
                    {budgetChange.toFixed(2)} ({budgetChange > 0 ? '+' : ''}
                    {budgetChangePercent.toFixed(1)}%)
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Initial Status */}
            <Box>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
                Initial Status
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box
                  onClick={() => setInitialStatus('PAUSED')}
                  sx={{
                    flex: 1,
                    p: 2,
                    border: 2,
                    borderColor: initialStatus === 'PAUSED' ? 'warning.main' : 'divider',
                    bgcolor: initialStatus === 'PAUSED' ? 'warning.50' : 'background.paper',
                    borderRadius: 2,
                    cursor: 'pointer',
                    textAlign: 'center',
                    transition: 'all 0.2s',
                  }}
                >
                  <Typography variant="body2" fontWeight="medium">
                    Paused
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Safe - Review before activating
                  </Typography>
                </Box>
                <Box
                  onClick={() => setInitialStatus('ACTIVE')}
                  sx={{
                    flex: 1,
                    p: 2,
                    border: 2,
                    borderColor: initialStatus === 'ACTIVE' ? 'success.main' : 'divider',
                    bgcolor: initialStatus === 'ACTIVE' ? 'success.50' : 'background.paper',
                    borderRadius: 2,
                    cursor: 'pointer',
                    textAlign: 'center',
                    transition: 'all 0.2s',
                  }}
                >
                  <Typography variant="body2" fontWeight="medium">
                    Active
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Starts running immediately
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Summary */}
            <Box sx={{ p: 2, bgcolor: 'secondary.50', borderRadius: 1, border: 1, borderColor: 'secondary.200' }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckIcon fontSize="small" />
                Duplication Summary
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, fontSize: '0.875rem' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">New Ad Name:</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {newAdName || '(not set)'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">AdSet Budget:</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    ${currentBudget.toFixed(2)} → ${newBudget.toFixed(2)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Initial Status:</Typography>
                  <Typography
                    variant="body2"
                    fontWeight="medium"
                    color={initialStatus === 'ACTIVE' ? 'success.main' : 'warning.main'}
                  >
                    {initialStatus}
                  </Typography>
                </Box>
              </Box>
            </Box>
          {/* Actions */}
          <Box
            sx={{
              pt: 3,
              mt: 2,
              borderTop: 1,
              borderColor: 'divider',
              display: 'flex',
              gap: 2,
              justifyContent: 'flex-end',
            }}
          >
            <Button onClick={onClose} disabled={isSubmitting} variant="outlined">
              Cancel
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleSubmit}
              disabled={isSubmitting || !newAdName.trim()}
              startIcon={isSubmitting ? <CircularProgress size={16} /> : <CopyIcon />}
            >
              {isSubmitting ? 'Creating...' : 'Create Duplicate Ad'}
            </Button>
          </Box>
        </Box>
      )}
    </Modal>
  );
};

export default DuplicateAdModal;
